/**
 * Audit logs data fetching hook.
 * 
 * @module hooks/useAuditLogs
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import type { AdminAuditLog, PaginatedResponse, AuditLogQueryOptions } from '../types';

/**
 * Fetches paginated admin audit logs.
 * 
 * @param options - Query options
 * @returns Query result with paginated audit logs
 */
export function useAuditLogs(options: AuditLogQueryOptions = {}) {
  const {
    page = 1,
    pageSize = 50,
    adminUserId,
    actionType,
    resourceType,
    dateFrom,
    dateTo,
    sortBy = 'created_at',
    sortOrder = 'desc',
  } = options;

  return useQuery({
    queryKey: ['audit-logs', options],
    queryFn: async (): Promise<PaginatedResponse<AdminAuditLog>> => {
      let query = supabase
        .from('admin_audit_logs')
        .select('*', { count: 'exact' });

      // Apply filters
      if (adminUserId) {
        query = query.eq('admin_user_id', adminUserId);
      }

      if (actionType) {
        query = query.eq('action_type', actionType);
      }

      if (resourceType) {
        query = query.eq('resource_type', resourceType);
      }

      if (dateFrom) {
        query = query.gte('created_at', dateFrom);
      }

      if (dateTo) {
        query = query.lte('created_at', dateTo);
      }

      // Apply sorting
      query = query.order(sortBy, { ascending: sortOrder === 'asc' });

      // Apply pagination
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;
      query = query.range(from, to);

      const { data, error, count } = await query;

      if (error) {
        throw new Error(`Failed to fetch audit logs: ${error.message}`);
      }

      return {
        data: (data as AdminAuditLog[]) || [],
        count: count || 0,
        page,
        pageSize,
        totalPages: Math.ceil((count || 0) / pageSize),
      };
    },
  });
}

/**
 * Fetches unique action types for filter dropdown.
 * 
 * @returns Query result with action types
 */
export function useAuditActionTypes() {
  return useQuery({
    queryKey: ['audit-action-types'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('admin_audit_logs')
        .select('action_type');

      if (error) {
        throw new Error(`Failed to fetch action types: ${error.message}`);
      }

      // Extract unique action types
      const actionTypes = [...new Set(data?.map((l: { action_type: string }) => l.action_type))] as string[];
      return actionTypes.sort();
    },
  });
}

/**
 * Fetches unique resource types for filter dropdown.
 * 
 * @returns Query result with resource types
 * @see CSDEV-142 - Audit logs page filters
 */
export function useAuditResourceTypes() {
  return useQuery({
    queryKey: ['audit-resource-types'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('admin_audit_logs')
        .select('resource_type')
        .not('resource_type', 'is', null);

      if (error) {
        throw new Error(`Failed to fetch resource types: ${error.message}`);
      }

      // Extract unique resource types
      const resourceTypes = [...new Set(data?.map((l: { resource_type: string }) => l.resource_type))] as string[];
      return resourceTypes.sort();
    },
  });
}

/**
 * Fetches unique admin emails for filter dropdown.
 * 
 * @returns Query result with admin emails
 * @see CSDEV-142 - Audit logs page filters
 */
export function useAuditAdmins() {
  return useQuery({
    queryKey: ['audit-admins'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('admin_audit_logs')
        .select('admin_user_id, admin_email');

      if (error) {
        throw new Error(`Failed to fetch admin users: ${error.message}`);
      }

      // Extract unique admins
      const adminMap = new Map<string, string>();
      data?.forEach((l: { admin_user_id: string; admin_email: string }) => {
        adminMap.set(l.admin_user_id, l.admin_email);
      });

      return Array.from(adminMap.entries()).map(([id, email]) => ({ id, email }));
    },
  });
}
